// Data Export Service for Way2Will
// Handles exporting user data as JSON, CSV, or ZIP files

import JSZip from 'jszip';

// Types for exportable data
export interface ExportableData {
    tasks: any[];
    events: any[];
    projects: any[];
    habits: any[];
    goals: any[];
    contexts: any[];
    voiceCaptures: any[];
    exportDate: string;
    version: string;
}

/**
 * Export all data as JSON
 */
export function exportAsJSON(data: ExportableData): string {
    return JSON.stringify(data, null, 2);
}

/**
 * Convert array of objects to CSV string
 */
function arrayToCSV(data: any[]): string {
    if (!data || data.length === 0) {
        return '';
    }

    // Get headers from first object
    const headers = Object.keys(data[0]);

    // Create CSV rows
    const csvRows = [
        headers.join(','), // Header row
        ...data.map(item =>
            headers.map(header => {
                const value = item[header];
                // Handle null/undefined
                if (value === null || value === undefined) return '';
                // Handle objects/arrays by stringifying
                if (typeof value === 'object') return `"${JSON.stringify(value).replace(/"/g, '""')}"`;
                // Handle strings with commas or quotes
                if (typeof value === 'string' && (value.includes(',') || value.includes('"') || value.includes('\n'))) {
                    return `"${value.replace(/"/g, '""')}"`;
                }
                return String(value);
            }).join(',')
        )
    ];

    return csvRows.join('\n');
}

/**
 * Export all data as separate CSV files in a ZIP
 */
export async function exportAsCSVZip(data: ExportableData): Promise<Blob> {
    const zip = new JSZip();

    // Add each data type as a separate CSV file
    if (data.tasks?.length > 0) {
        zip.file('tasks.csv', arrayToCSV(data.tasks));
    }
    if (data.events?.length > 0) {
        zip.file('events.csv', arrayToCSV(data.events));
    }
    if (data.projects?.length > 0) {
        zip.file('projects.csv', arrayToCSV(data.projects));
    }
    if (data.habits?.length > 0) {
        zip.file('habits.csv', arrayToCSV(data.habits));
    }
    if (data.goals?.length > 0) {
        zip.file('goals.csv', arrayToCSV(data.goals));
    }
    if (data.contexts?.length > 0) {
        zip.file('contexts.csv', arrayToCSV(data.contexts));
    }
    if (data.voiceCaptures?.length > 0) {
        zip.file('voice_captures.csv', arrayToCSV(data.voiceCaptures));
    }

    // Add metadata file
    zip.file('export_info.json', JSON.stringify({
        exportDate: data.exportDate,
        version: data.version,
        counts: {
            tasks: data.tasks?.length || 0,
            events: data.events?.length || 0,
            projects: data.projects?.length || 0,
            habits: data.habits?.length || 0,
            goals: data.goals?.length || 0,
            contexts: data.contexts?.length || 0,
            voiceCaptures: data.voiceCaptures?.length || 0,
        }
    }, null, 2));

    return await zip.generateAsync({ type: 'blob' });
}

/**
 * Trigger browser download of a file
 */
export function triggerDownload(blob: Blob, filename: string): void {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
}

/**
 * Export all data as JSON and trigger download
 */
export function downloadAsJSON(data: ExportableData): void {
    const json = exportAsJSON(data);
    const blob = new Blob([json], { type: 'application/json' });
    const date = new Date().toISOString().split('T')[0];
    triggerDownload(blob, `way2will-export-${date}.json`);
}

/**
 * Export all data as CSV ZIP and trigger download
 */
export async function downloadAsCSVZip(data: ExportableData): Promise<void> {
    const zip = await exportAsCSVZip(data);
    const date = new Date().toISOString().split('T')[0];
    triggerDownload(zip, `way2will-export-${date}.zip`);
}

/**
 * Get export data summary for UI display
 */
export function getExportSummary(data: ExportableData): {
    totalItems: number;
    breakdown: { type: string; count: number }[];
} {
    const breakdown = [
        { type: 'Tasks', count: data.tasks?.length || 0 },
        { type: 'Events', count: data.events?.length || 0 },
        { type: 'Projects', count: data.projects?.length || 0 },
        { type: 'Habits', count: data.habits?.length || 0 },
        { type: 'Goals', count: data.goals?.length || 0 },
        { type: 'Contexts', count: data.contexts?.length || 0 },
        { type: 'Voice Captures', count: data.voiceCaptures?.length || 0 },
    ].filter(item => item.count > 0);

    const totalItems = breakdown.reduce((sum, item) => sum + item.count, 0);

    return { totalItems, breakdown };
}
